import React from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { CreditSummaryBureauReport, PaymentStatus } from '@/graphql/__generated__/onboarding';
import { getPaymentStatusText } from './mapUtils';
import { formatMoney } from '@/utils/utils';
import { Info } from 'react-feather';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import ExpandableTable from './ExpandableTable';

interface CreditSummaryProps {
	creditSummary?: CreditSummaryBureauReport | null;
}

const PAYMENT_STATUS_ORDER: PaymentStatus[] = [
	PaymentStatus.CODE_UR,
	PaymentStatus.CODE_00,
	PaymentStatus.CODE_01,
	PaymentStatus.CODE_02,
	PaymentStatus.CODE_03,
	PaymentStatus.CODE_04,
	PaymentStatus.CODE_05,
	PaymentStatus.CODE_06,
	PaymentStatus.CODE_07,
	PaymentStatus.CODE_96,
	PaymentStatus.CODE_97,
	PaymentStatus.CODE_99,
	PaymentStatus.CODE_TOTAL
];

const CreditSummary: React.FC<CreditSummaryProps> = ({ creditSummary }) => {
	const t = useTranslations('CreditBureau');

	if (!creditSummary || !creditSummary.accounts || creditSummary.accounts.length === 0) {
		return null;
	}

	const formatMoneyValue = (money: string | null | undefined): string => {
		if (!money) return '0.00';
		return formatMoney(money);
	};

	const accountsByStatus = new Map<PaymentStatus, (typeof creditSummary.accounts)[0]>();
	creditSummary.accounts.forEach((account) => {
		if (account?.paymentStatus) {
			accountsByStatus.set(account.paymentStatus, account);
		}
	});

	const hasData = (account: (typeof creditSummary.accounts)[0] | undefined): boolean => {
		if (!account) return false;
		const openCount = account.openAccounts?.numberOfAccounts || 0;
		const closedCount = account.closedAccounts?.numberOfAccounts || 0;
		return openCount > 0 || closedCount > 0;
	};

	const rows = PAYMENT_STATUS_ORDER.filter((status) => {
		const account = accountsByStatus.get(status);
		return status === PaymentStatus.CODE_TOTAL || hasData(account);
	});

	const formatDecimal = (value: string | null | undefined): string => {
		if (!value) return '0.00';
		const num = parseFloat(value);
		return isNaN(num) ? '0.00' : num.toFixed(2);
	};

	const renderTotalIndicators = () => {
		const indicators = [
			{
				key: 'utilizationPrePandemic',
				value: formatDecimal(creditSummary.utilizationPrePandemic),
				nameKey: 'UtilizationPrePandemic',
				descriptionKey: 'UtilizationPrePandemicDescription'
			},
			{
				key: 'utilizationPostPandemic',
				value: formatDecimal(creditSummary.utilizationPostPandemic),
				nameKey: 'UtilizationPostPandemic',
				descriptionKey: 'UtilizationPostPandemicDescription'
			},
			{
				key: 'monthsOverdue',
				value: creditSummary.monthsOverdue?.toString() || '0',
				nameKey: 'MonthsOverdue',
				descriptionKey: 'MonthsOverdueDescription'
			},
			{
				key: 'yearsInCreditBureau',
				value: creditSummary.yearsInCreditBureau?.toString() || '0',
				nameKey: 'YearsInCreditBureau',
				descriptionKey: 'YearsInCreditBureauDescription'
			},
			{
				key: 'lastSixMonthsInquiries',
				value: creditSummary.lastSixMonthsInquiries?.toString() || '0',
				nameKey: 'LastSixMonthsInquiries',
				descriptionKey: 'LastSixMonthsInquiriesDescription'
			}
		];

		return (
			<div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
				<h4 className="text-md font-semibold mb-4">{t('TotalIndicators')}</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{indicators.map((indicator) => (
						<div key={indicator.key} className="flex flex-col gap-1">
							<div className="flex items-center gap-2">
								<span className="text-sm font-medium">
									{t(toTranslationKey(indicator.nameKey))}:
								</span>
								<Tooltip
									content={t(toTranslationKey(indicator.descriptionKey))}
									position="top"
									id={`${indicator.key}-tooltip`}
								>
									<Info color={BRAND_COLORS.brand600} size={16} className="cursor-pointer" />
								</Tooltip>
							</div>
							<span className="text-md font-semibold text-gray-700">{indicator.value}</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderTable = () => (
		<table
			className="w-full border-collapse border border-gray-300 text-sm"
			style={{ minWidth: '100%' }}
		>
			<thead>
				<tr>
					<th
						rowSpan={2}
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-semibold text-left"
					>
						{t('MOP')}
					</th>
					<th
						colSpan={6}
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-semibold text-left"
					>
						{t('OpenAccounts')}
					</th>
					<th
						colSpan={5}
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-semibold text-left"
					>
						{t('ClosedAccounts')}
					</th>
				</tr>
				<tr>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('NumberOfAccounts')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('LimitBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('MaximumBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('CurrentBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('DueBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('MonthlyPayment')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('NumberOfAccounts')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('LimitBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('MaximumBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('CurrentBalance')}
					</th>
					<th
						style={{ backgroundColor: BRAND_COLORS.brand500 }}
						className="border border-white text-white px-4 py-2 font-medium text-left text-xs"
					>
						{t('Amount')}
					</th>
				</tr>
			</thead>
			<tbody>
				{rows.map((status, index) => {
					const account = accountsByStatus.get(status);
					const isTotal = status === PaymentStatus.CODE_TOTAL;
					const rowBackgroundColor = isTotal
						? BRAND_COLORS.brand50
						: index % 2 === 0
							? 'white'
							: BRAND_COLORS.brand50;

					return (
						<tr
							key={status}
							style={{ backgroundColor: rowBackgroundColor }}
							className={isTotal ? 'font-semibold' : ''}
						>
							<td className="border border-gray-300 px-4 py-2 font-semibold text-center">
								{getPaymentStatusText(status, t)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{account?.openAccounts?.numberOfAccounts || 0}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.openAccounts?.limitBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.openAccounts?.maximumBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.openAccounts?.currentBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.openAccounts?.amount)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.openAccounts?.monthlyPayment)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{account?.closedAccounts?.numberOfAccounts || 0}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.closedAccounts?.limitBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.closedAccounts?.maximumBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.closedAccounts?.currentBalance)}
							</td>
							<td className="border border-gray-300 px-4 py-2 text-right">
								{formatMoneyValue(account?.closedAccounts?.amount)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);

	return (
		<ExpandableTable title={t('CreditSummaryTitle')} headerContent={renderTotalIndicators()}>
			{renderTable()}
		</ExpandableTable>
	);
};

export default CreditSummary;
